import { httpsCallable } from "firebase/functions";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import dayjs from "dayjs";
import updateLocale from "dayjs/plugin/updateLocale";
import { db, functions } from "../data/firebase";
import { ROLES } from "./permissions";

// The app treats Saturday as the first day of the week (see Calendar and
// SeniorTutorPage). Set it here too so the Covers page never depends on which
// page loaded first.
dayjs.extend(updateLocale);
dayjs.updateLocale("en", { weekStart: 6 });

export const COVER_MANAGER_ROLES = [ROLES.ADMIN, ROLES.HEAD_TUTOR, ROLES.SENIOR_TUTOR];

export const getWeekStart = (date) => dayjs(date).startOf("week");
export const getWeekKey = (date) => getWeekStart(date).format("YYYY-MM-DD");

/** Who is assigned as senior tutor for the week containing `date`. */
export const fetchSeniorTutorAssignment = async (date = new Date()) => {
  const snap = await getDoc(doc(db, "seniorTutorAssignments", getWeekKey(date)));
  return snap.exists() ? snap.data() : null;
};

export const canManageCovers = (role, uid, assignedSeniorTutorId) =>
  COVER_MANAGER_ROLES.includes(role) || (!!assignedSeniorTutorId && assignedSeniorTutorId === uid);

const call = (name) => async (payload) => {
  const fn = httpsCallable(functions, name);
  const res = await fn(payload);
  return res.data;
};

export const createCoverGroup = call("createCoverGroup");
export const respondToCoverGroup = call("respondToCoverGroup");
export const updateCoverGroupInvites = call("updateCoverGroupInvites");
export const cancelCoverGroup = call("cancelCoverGroup");
export const saveCoverTemplates = call("saveCoverTemplates");

/** Message templates. Placeholders are filled per recipient by the server. */
export const TEMPLATE_PLACEHOLDERS = [
  { key: "firstName", label: "Tutor's first name", sample: "Kyle" },
  { key: "originalTutor", label: "Tutor needing cover", sample: "Yilin Zhang" },
  { key: "sessions", label: "Session summary", sample: "Sat 12 Sep, 10:00 am – 1:00 pm (3 sessions)" },
  { key: "count", label: "Number of sessions", sample: "3" },
  { key: "day", label: "Day of first session", sample: "Saturday 12 September" },
];

export const DEFAULT_TEMPLATES = [
  "Hi {firstName}, are you able to cover {originalTutor}'s sessions on {day}? Details are on the card below.",
  "Hi {firstName}, we need cover for {sessions}. Can you help? Reply on the card.",
];

export const renderTemplate = (text, vars) =>
  (text || "").replace(/\{(\w+)\}/g, (m, key) =>
    vars?.[key] === undefined || vars?.[key] === null ? m : String(vars[key])
  );

export const pickRandom = (list, avoid) => {
  if (!list?.length) return "";
  if (list.length === 1) return list[0];
  const options = list.filter((t) => t !== avoid);
  return options[Math.floor(Math.random() * options.length)];
};

/** Live subscription to saved templates; falls back to DEFAULT_TEMPLATES when none saved. */
export const subscribeCoverTemplates = (cb) =>
  onSnapshot(
    doc(db, "settings", "coverMessages"),
    (snap) => {
      const saved = snap.exists() ? snap.data().templates : null;
      cb(Array.isArray(saved) && saved.length ? saved : DEFAULT_TEMPLATES, !!(saved && saved.length));
    },
    () => cb(DEFAULT_TEMPLATES, false)
  );

export const STATUS_LABELS = {
  open: "Open",
  taken: "Taken",
  cancelled: "Cancelled",
  expired: "Expired",
};

export const RESPONSE_LABELS = {
  pending: "No reply",
  accepted: "Accepted",
  declined: "Declined",
};

export const fmtTime = (iso) => dayjs(iso).format("h:mm a");
export const fmtDay = (iso) => dayjs(iso).format("ddd D MMM");
export const fmtRange = (startIso, endIso) => `${fmtTime(startIso)} – ${fmtTime(endIso)}`;

export const summariseSessions = (sessions = []) => {
  if (!sessions.length) return "";
  const sorted = [...sessions].sort((a, b) => a.startDateTime.localeCompare(b.startDateTime));
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const count = sessions.length === 1 ? "1 session" : `${sessions.length} sessions`;
  return `${fmtDay(first.startDateTime)}, ${fmtRange(first.startDateTime, last.endDateTime)} (${count})`;
};

export const errorMessage = (err) =>
  err?.message?.replace(/^.*?:\s*/, "") || "Something went wrong";
